import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { API_URL } from '../config';

export default function ImageUploader({ initialImage, onImageUploaded }) {
  const [imagePreview, setImagePreview] = useState(initialImage || null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Check if the file is a HEIC/HEIF file using multiple detection methods
      const isHeic = 
        file.type === 'image/heic' || 
        file.type === 'image/heif' ||
        file.name.toLowerCase().endsWith('.heic') || 
        file.name.toLowerCase().endsWith('.heif');
      
      let processedFile = file;
      
      if (isHeic) {
        // Show loading toast
        toast.loading("Converting iPhone HEIC image...", { id: "heic-conversion" });
        
        try {
          // Dynamically import heic2any to prevent issues with server-side rendering
          const heic2anyModule = await import('heic2any');
          const heic2any = heic2anyModule.default;
          
          // Convert HEIC to JPEG with error handling
          const jpegBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.8,
            // Add a reasonable timeout to prevent infinite processing
            timeout: 30000
          });
          
          // Ensure we have a valid blob result
          if (!jpegBlob) {
            throw new Error('Conversion resulted in an empty file');
          }
          
          // Create a new file from the JPEG blob
          processedFile = new File(
            [jpegBlob], 
            file.name.replace(/\.(heic|heif)$/i, '.jpg'), 
            { type: 'image/jpeg' }
          );
          
          toast.success("HEIC image converted successfully", { id: "heic-conversion" });
        } catch (conversionError) {
          console.error("Error converting HEIC image:", conversionError);
          
          // Clear the loading toast
          toast.dismiss("heic-conversion");
          
          // Show detailed error and suggestion to the user
          toast.error(
            "Unable to convert HEIC image. Please convert it to JPEG or PNG before uploading, or try a different image.",
            { duration: 5000 }
          );
          
          setIsUploading(false);
          return;
        }
      }

      // Validate file size (10MB max)
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
      if (processedFile.size > MAX_FILE_SIZE) {
        toast.error("Image is too large. Maximum size is 10MB.");
        setIsUploading(false);
        return;
      }

      // Create a preview with error handling
      try {
        const previewUrl = URL.createObjectURL(processedFile);
        setImagePreview(previewUrl);
        setUploadProgress(10);
      } catch (previewError) {
        console.error("Error creating preview:", previewError);
        toast.error("Failed to generate image preview");
        // Continue with upload even if preview fails
      }

      // Get presigned URL from API using REST endpoint
      const response = await fetch(`${API_URL}/api/where-to-go/get-upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // This sends HTTP-only cookies automatically
        body: JSON.stringify({
          fileName: processedFile.name,
          fileType: processedFile.type,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to get upload URL');
      }

      const { uploadUrl, imageUrl: fileUrl } = await response.json();
      setUploadProgress(30);

      // Upload to S3 with error handling
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: processedFile,
        headers: {
          'Content-Type': processedFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.statusText} (${uploadResponse.status})`);
      }

      setUploadProgress(90);

      // Update form with the new image URL
      onImageUploaded(fileUrl);
      setUploadProgress(100);
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadProgress(0);
      setImagePreview(initialImage);
      toast.error(error.message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [initialImage, onImageUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.heic', '.heif', '.webp']
    },
    maxFiles: 1,
    multiple: false
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-sky-500 bg-sky-50' : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        {imagePreview ? (
          <div className="space-y-2">
            <img
              src={imagePreview}
              alt="Preview"
              className="mx-auto max-h-48 rounded-lg"
            />
            <p className="text-sm text-gray-500">
              Click or drag to replace image
            </p>
            {isUploading && (
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-sky-600 h-2.5 rounded-full transition-all duration-300 ease-in-out" 
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="text-sm text-gray-600">
              <span className="text-sky-600 hover:text-sky-500">
                Click to upload
              </span>{' '}
              or drag and drop
            </div>
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF, HEIC up to 10MB
            </p>
            {isUploading && (
              <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                <div 
                  className="bg-sky-600 h-2.5 rounded-full transition-all duration-300 ease-in-out" 
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 